import { useState, useEffect } from "react";
import clsx from "clsx";
// Helpers
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { accountFormat } from "@/Common/helpers/number";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
// Components
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { ProductsCombinedTable } from "./ProductsCombinedTable";
import { ProductsSeparatedTable } from "./ProductsSeparatedTable";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
// MUI
import { Stack } from "@mui/material";
// Types and constants
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { CustomColDef } from "@/Washouts/types/commons";
import {
	ChargebacksTableData,
	ProductConversionData,
	ProductConversionRecord,
	ProductConversionWrapper,
	ProductConversionsTableProps,
	Row,
} from "@/Washouts/types/finance";
// Styles
import "@/Washouts/assets/styles/ProductConversionsTable.css";

const DEFAULT_TOP_TABLE_ROWS = 3;
const DEFAULT_BOTTOM_TABLE_ROWS = 5;

function getPenetrationFromProductConversion(
	type: keyof Omit<ProductConversionData, "globalGrossAndUnits">,
	productConversions?: ProductConversionData
) {
	if (!productConversions) {
		return "0.00";
	}
	let penetration = 0;
	if (productConversions[type]) {
		const set = productConversions[type];
		if (set) {
			penetration = set.penetration ?? 0;
		}
	}
	return Number(penetration).toFixed(2);
}

function getChargebacksTable(totals?: ProductConversionWrapper) {
	const chargebacksTableData: ChargebacksTableData[] = [];
	if (totals?.products?.length) {
		const chargebacks = totals.products.find((product) => product.product?.toUpperCase() === "CHARGEBACKS");
		if (chargebacks) {
			chargebacksTableData.push({
				_id: `${chargebacks._id}_header`,
				product: chargebacks.product,
				quantity: "(Quantity)",
				penetration: chargebacks.penetrationLabel ?? "-",
				unitContracts: "(Quantity)",
			});
			chargebacksTableData.push({
				_id: chargebacks._id,
				product: chargebacks.total ? String(chargebacks.total) : "-",
				quantity: String(chargebacks.quantity),
				penetration: String(chargebacks.penetration),
				unitContracts: chargebacks?.unitContracts ? String(chargebacks?.unitContracts) : "-",
			});
		}
	}
	return chargebacksTableData;
}

function getCombinedColumns(totalPenetration: string): CustomColDef[] {
	return [
		{
			field: "product",
			headerName: "Grosses",
			sortable: false,
			width: 115,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} />,
			cellClassName: getCellClassNameFn([], { noWrap: true }),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return (
						<TypographyCell params={params} hints={{ noWrap: true }}>
							Product Penetration
						</TypographyCell>
					);
				}
				const isNumber = isNumeric(params.formattedValue ?? params.value);
				return (
					<TypographyCell
						className={clsx({ "text-right": isNumber })}
						render={isNumber ? "absolute" : "default"}
						params={params}
						hints={{
							isNegative: isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1,
							isMoney: isNumber,
						}}
					/>
				);
			},
		},
		{
			field: "",
			headerName: "",
			sortable: false,
			flex: 1,
			disableReorder: true,
			resizable: false,
			headerClassName: `header-empty-title`,
			cellClassName: getCellClassNameFn(),
		},
		{
			field: "quantity",
			headerName: "SVC",
			sortable: false,
			width: 100,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				const isNumber = isNumeric(params.formattedValue ?? params.value);
				const isNegative = isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1;
				return <TypographyCell params={params} dashed={false} hints={{ isNegative }} />;
			},
		},
		{
			field: "unitContracts",
			headerName: "SVC Units",
			sortable: false,
			width: 120,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				const isNumber = isNumeric(params.formattedValue ?? params.value);
				const isNegative = isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1;
				if (params?.id.toString().includes("footer")) {
					return (
						<TypographyCell params={params} hints={{ isNegative }} noWrap sx={{ width: 1, textAlign: "left" }}>
							{totalPenetration}
						</TypographyCell>
					);
				}
				return <TypographyCell params={params} hints={{ isNegative }} />;
			},
		},
		{
			field: "penetration",
			headerName: "Conversions",
			type: "number",
			sortable: false,
			width: 120,
			align: "right",
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} />,
			cellClassName: getCellClassNameFn([], { isNegative: false }),
			renderCell: (params) => {
				let useValue = params.value;
				let isPercentage = false;
				let isNegative = false;
				if (typeof params.id === "string" && (params.id.includes("_header") || params.id.startsWith("root_"))) {
					useValue = params.row.penetration;
				}

				if (isFooterCell(params) && params?.field === "penetration") {
					useValue = "";
				}

				if (isNumeric(useValue)) {
					isPercentage = true;
					if (!params?.id.toString().includes("footer")) {
						useValue = useValue * 100;
					}
					useValue = accountFormat(useValue);
					isNegative = Math.sign(Number(useValue)) === -1;
				}

				return (
					<TypographyCell params={params} hints={{ isPercentage, isNegative }}>
						{useValue === "-" ? "0.00" : useValue}
					</TypographyCell>
				);
			},
		},
	];
}

function getSeparatedGrossesColumns(): CustomColDef[] {
	return [
		{
			field: "productNew",
			headerName: "New Grosses",
			sortable: false,
			width: 80,
			maxWidth: 80,
			resizable: false,
			headerClassName: `${HEADER_CLASS_NAME}`,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} />,
			cellClassName: getCellClassNameFn([], { noWrap: true }),
			renderCell: (params) => {
				const isNumber = isNumeric(params.formattedValue ?? params.value);
				const isNegative = isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1;
				if (isFooterCell(params)) {
					return (
						<TypographyCell params={params} hints={{ noWrap: true }}>
							Total Product Penetration
						</TypographyCell>
					);
				}
				return (
					<TypographyCell
						className={clsx({ "text-right": isNumber })}
						render={isNumber ? "absolute" : "default"}
						params={params}
						hints={{ isNegative }}
					/>
				);
			},
		},
		{
			field: "productUsed",
			headerName: "Used Grosses",
			sortable: false,
			width: 80,
			maxWidth: 80,
			resizable: false,
			headerClassName: `${HEADER_CLASS_NAME}`,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return <></>;
				}
				const isNumber = isNumeric(params.formattedValue ?? params.value);
				const isNegative = isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1;
				return (
					<TypographyCell
						className={clsx({ "text-right": isNumber })}
						render={isNumber ? "absolute" : "default"}
						params={params}
						hints={{ isNegative }}
					/>
				);
			},
		},
	];
}

function getSeparatedConversionColumns(): CustomColDef[] {
	return [
		{
			field: "penetrationNew",
			headerName: "New Conversion",
			type: "number",
			sortable: false,
			flex: 1,
			width: 85,
			maxWidth: 85,
			resizable: false,
			align: "right",
			headerClassName: `${HEADER_CLASS_NAME}`,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} right />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				let useValue = params.formattedValue ?? params.value;
				let isPercentage = false;
				let isNegative = false;
				if (params.id.toString().includes("_header") || params.id.toString().startsWith("root_")) {
					useValue = params.row.penetrationNew;
				}
				if (params?.id.toString().includes("footer")) {
					useValue = "";
				}
				if (isNumeric(useValue)) {
					isPercentage = true;
					if (!params?.id.toString().includes("footer")) {
						useValue = Math.round(useValue * 100);
					}
					useValue = accountFormat(useValue);
					isNegative = Math.sign(Number(useValue)) === -1;
				}
				return (
					<TypographyCell params={params} hints={{ isPercentage, isNegative }}>
						{useValue === "-" ? "0.00" : useValue}
					</TypographyCell>
				);
			},
		},
		{
			field: "penetrationUsed",
			headerName: "Used Conversion",
			type: "number",
			sortable: false,
			align: "right",
			width: 85,
			maxWidth: 85,
			flex: 1,
			resizable: false,
			headerClassName: `${HEADER_CLASS_NAME}`,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} right />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				let useValue = params.formattedValue ?? params.value;
				let isPercentage = false;
				let isNegative = false;
				if (params.id.toString().includes("_header") || params.id.toString().startsWith("root_")) {
					useValue = params.row.penetrationUsed;
				}
				if (isFooterCell(params) && params?.field === "penetrationUsed") {
					useValue = "";
				}
				if (isNumeric(useValue)) {
					isPercentage = true;
					if (!params?.id.toString().includes("footer")) {
						useValue = Math.round(useValue * 100);
					}
					useValue = accountFormat(useValue);
					isNegative = Math.sign(Number(useValue)) === -1;
				}
				return (
					<TypographyCell params={params} hints={{ isPercentage, isNegative }}>
						{useValue === "-" ? "0.00" : useValue}
					</TypographyCell>
				);
			},
		},
	];
}

function getSeparatedColumns(newPenetration: string, usedPenetration: string): CustomColDef[] {
	return [
		...getSeparatedGrossesColumns(),
		{
			field: "quantityNew",
			headerName: "New SVC",
			sortable: false,
			width: 65,
			maxWidth: 65,
			resizable: false,
			headerClassName: `${HEADER_CLASS_NAME}`,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				const isNumber = isNumeric(params.formattedValue ?? params.value);
				const isNegative = isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1;
				return <TypographyCell params={params} dashed={false} hints={{ isNegative }} />;
			},
		},
		{
			field: "unitContractsNew",
			headerName: "New SVC Units",
			sortable: false,
			width: 65,
			maxWidth: 65,
			resizable: false,
			headerClassName: `${HEADER_CLASS_NAME}`,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} lineClamp="3" />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				let isNumber = false;
				let isNegative = false;
				if (isFooterCell(params)) {
					isNumber = isNumeric(newPenetration);
					isNegative = isNumber && Math.sign(Number(newPenetration)) === -1;
					return (
						<TypographyCell params={params} hints={{ isNegative }}>
							{newPenetration}
						</TypographyCell>
					);
				}
				isNumber = isNumeric(params.formattedValue ?? params.value);
				isNegative = isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1;
				return <TypographyCell params={params} hints={{ isNegative }} />;
			},
		},
		{
			field: "quantityUsed",
			headerName: "Used SVC",
			sortable: false,
			width: 65,
			maxWidth: 65,
			resizable: false,
			headerClassName: `${HEADER_CLASS_NAME}`,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				const isNumber = isNumeric(params.formattedValue ?? params.value);
				const isNegative = isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1;
				return <TypographyCell params={params} dashed={false} hints={{ isNegative }} />;
			},
		},
		{
			field: "unitContractsUsed",
			headerName: "Used SVC Units",
			sortable: false,
			width: 65,
			maxWidth: 65,
			resizable: false,
			headerClassName: `${HEADER_CLASS_NAME}`,
			renderHeader: (params) => <TypographyHeader className="pt-[2px]" params={params} lineClamp="3" />,
			cellClassName: getCellClassNameFn(),
			renderCell: (params) => {
				let isNumber = false;
				let isNegative = false;
				if (isFooterCell(params)) {
					isNumber = isNumeric(usedPenetration);
					isNegative = isNumber && Math.sign(Number(usedPenetration)) === -1;
					return (
						<TypographyCell params={params} hints={{ isNegative }}>
							{usedPenetration}
						</TypographyCell>
					);
				}
				isNumber = isNumeric(params.formattedValue ?? params.value);
				isNegative = isNumber && Math.sign(Number(params.formattedValue ?? params.value)) === -1;
				return <TypographyCell params={params} hints={{ isNegative }} />;
			},
		},
		...getSeparatedConversionColumns(),
	];
}

function ternary<T>(criteria: boolean, valIfTrue: T, valIfFalse: T) {
	return criteria ? valIfTrue : valIfFalse;
}

function nullish<T>(val: T | null | undefined, defaultVal: T | string) {
	return val ?? defaultVal;
}

function processData({ data, combined }: { data?: ProductConversionData; combined: boolean }) {
	const dataToShow: Row[] = [];
	const totals = (JSON.parse(JSON.stringify(data?.totals.products)) ?? []) as ProductConversionRecord[];
	const chargebackElement = totals.shift();

	const productField = ternary(combined, "product", "productNew");
	const quantityField = ternary(combined, "quantity", "quantityNew");
	const penetrationField = ternary(combined, "penetration", "penetrationNew");
	const childRows = [];

	// Set up row parent for products
	let index = ternary(combined, 1, 0);
	totals.forEach((element, key) => {
		const row: Row = {
			_id: "root_" + element._id,
			hierarchy: ["root_" + element._id],
			index,
		};
		index++;
		row["product"] = element.product;
		row["quantity"] = "(Qty)";
		row["unitContracts"] = "(Qty)";
		row["penetration"] = element.penetrationLabel;
		// TEMPORARILY ADD TEXT TO ALL COLUMNS
		row["productNew"] = element.product;
		row["productUsed"] = element.product;
		row["quantityNew"] = "(Qty)";
		row["quantityUsed"] = "(Qty)";
		row["unitContractsNew"] = "(Qty)";
		row["unitContractsUsed"] = "(Qty)";
		row["penetrationNew"] = element.penetrationLabel;
		row["penetrationUsed"] = element.penetrationLabel;
		// REMOVE AFTER PRODUCTION
		dataToShow.push(row);
		const rowNodeId = `node_${key}_root_${element._id}`;
		const childRow: Row = {
			_id: rowNodeId,
			hierarchy: [row._id?.toString(), rowNodeId],
		};
		if (combined) {
			childRow[productField] = element.total;
			childRow[quantityField] = element.quantity;
			childRow[penetrationField] = element.penetration;
			childRow["unitContracts"] = nullish(element?.contracts, "0");
		} else {
			const newElement = data?.new.products[key];
			const usedElement = data?.preOwned.products[key];
			if (newElement && usedElement) {
				childRow["productNew"] = newElement.total;
				childRow["productUsed"] = usedElement.total;
				childRow["quantityNew"] = newElement.quantity;
				childRow["quantityUsed"] = usedElement.quantity;
				childRow["unitContractsNew"] = nullish(newElement.contracts, "-");
				childRow["unitContractsUsed"] = nullish(usedElement.contracts, "-");
				childRow["penetrationNew"] = newElement.penetration;
				childRow["penetrationUsed"] = usedElement.penetration;
			}
		}
		childRows.push(childRow);
	});

	if (combined && chargebackElement) {
		const chargebacksRootRowId = "root_" + chargebackElement._id;
		const chargebacksChildRowId = "node_0_" + chargebacksRootRowId;
		dataToShow.unshift({
			_id: "root_" + chargebackElement._id,
			product: chargebackElement.product,
			quantity: "(Qty)",
			unitContracts: "(Qty)",
			penetration: chargebackElement.penetrationLabel,
			hierarchy: [chargebacksRootRowId],
			index: 0,
		});
		childRows.unshift({
			_id: chargebacksChildRowId,
			product: chargebackElement.total,
			quantity: chargebackElement.quantity,
			penetration: chargebackElement.penetration,
			unitContracts: nullish(chargebackElement?.unitContracts, "-"),
			hierarchy: [chargebacksRootRowId, chargebacksChildRowId],
		});
	}
	childRows.length && childRows.forEach((row) => dataToShow.push(row));
	return dataToShow;
}

function ProductsTable(props: Readonly<ProductConversionsTableProps>) {
	const [data, setData] = useState<Row[]>([]);
	const [chargebacksData, setChargebacksData] = useState<ChargebacksTableData[]>([]);
	const combined = Boolean(props.combined);
	const [newPenetration, setNewPenetration] = useState("0");
	const [usedPenetration, setUsedPenetration] = useState("0");
	const [totalPenetration, setTotalPenetration] = useState("0");
	const maxTopTableRows = props.maxSeparatedConversionsTopTableRows ?? DEFAULT_TOP_TABLE_ROWS;
	const maxBottomTableRows = props.maxSeparatedConversionsBottomTableRows ?? DEFAULT_BOTTOM_TABLE_ROWS;

	const combinedColumns = [ID_GRID_COLDEF, ...getCombinedColumns(totalPenetration)];

	const separatedColumns = [ID_GRID_COLDEF, ...getSeparatedColumns(newPenetration, usedPenetration)];

	useEffect(() => {
		setNewPenetration(getPenetrationFromProductConversion("new", props?.rawData));
		setUsedPenetration(getPenetrationFromProductConversion("preOwned", props?.rawData));
		setTotalPenetration(getPenetrationFromProductConversion("totals", props?.rawData));
		setData(props?.data ?? []);
		setChargebacksData(props?.cbData ?? []);

		return () => {
			setData([]);
			setChargebacksData([]);
			setNewPenetration("0");
			setUsedPenetration("0");
			setTotalPenetration("0");
		};
	}, [props?.rawData, props?.data, props?.cbData]);

	return (
		<Stack className="!max-h-full">
			{combined ? (
				<ProductsCombinedTable {...props} columns={combinedColumns} rows={data} />
			) : (
				<ProductsSeparatedTable
					{...props}
					columns={{
						chargebacks: combinedColumns,
						data: separatedColumns,
					}}
					rows={{
						data,
						chargebacks: chargebacksData,
					}}
					maxSeparatedConversionsTopTableRows={maxTopTableRows}
					maxSeparatedConversionsBottomTableRows={maxBottomTableRows}
				/>
			)}
		</Stack>
	);
}

function ProductConversionsTable(props: Readonly<ProductConversionsTableProps>) {
	if (props.loading) {
		return <TableLoader smallTitle widthSmallTitle={250} content />;
	}
	return <>{props.dataPresent && <ProductsTable {...props} />}</>;
}

export { processData as processRawConversionsData, getChargebacksTable as getChargebacksFromRawConversionsData, ProductConversionsTable };
export default ProductConversionsTable;
